import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import useAdminData from '../components/shared/useAdminData';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import Modal from '../components/common/Modal';
import StatusBadge from '../components/common/StatusBadge';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './DataExport.css';

const { Skeleton } = SkeletonLoaderComponents;

const DataExport = () => {
  const { user } = useAdminAuth();
  const [formData, setFormData] = useState({
    dataType: 'users',
    format: 'csv',
    dateFrom: '',
    dateTo: '',
    fields: []
  });
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  // Fetch export jobs
  const { data: jobsData, loading, error: fetchError, refetch } = useAdminData(
    'data-export',
    API_ENDPOINTS.ADMIN.DATA_EXPORT,
    {}
  );
  const jobs = Array.isArray(jobsData) ? jobsData : (jobsData?.jobs || []);

  // Available fields for each data type
  const fieldOptions = {
    users: ['userId', 'email', 'name', 'phone', 'createdAt', 'subscription', 'groups'],
    analytics: ['userId', 'event', 'timestamp', 'metadata', 'sessionId'],
    transactions: ['transactionId', 'userId', 'amount', 'currency', 'status', 'timestamp'],
    audit_logs: ['logId', 'adminId', 'action', 'targetType', 'targetId', 'timestamp']
  };

  // Poll for active job status
  useEffect(() => {
    if (!activeJob) return;

    const pollInterval = setInterval(async () => {
      try {
        const session = await fetchAuthSession();
        // CRITICAL: Use idToken (not accessToken) for admin authorization
        // cognito:groups claim is ONLY in ID token, NOT in access token
        const token = session.tokens?.idToken?.toString();
        const response = await fetch(
          `${API_ENDPOINTS.ADMIN.DATA_EXPORT}?jobId=${activeJob.jobId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const job = await response.json();
          
          if (job.status === 'completed') {
            setSuccess('Export completed! Download link available below.');
            setActiveJob(null);
            refetch();
            clearInterval(pollInterval);
          } else if (job.status === 'failed') {
            setError(`Export failed: ${job.errorMessage}`);
            setActiveJob(null);
            clearInterval(pollInterval);
          }
        }
      } catch (err) {
        console.error('Error polling job status:', err);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [activeJob, refetch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDataTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      dataType: e.target.value,
      fields: [] // Reset fields when data type changes
    }));
  };

  const handleFieldToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.includes(field)
        ? prev.fields.filter(f => f !== field)
        : [...prev.fields, field]
    }));
  };

  const handleSelectAllFields = () => {
    const allFields = fieldOptions[formData.dataType] || [];
    setFormData(prev => ({
      ...prev,
      fields: allFields
    }));
  };

  const handleDeselectAllFields = () => {
    setFormData(prev => ({
      ...prev,
      fields: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (formData.fields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }

    if (formData.dateFrom && formData.dateTo && formData.dateFrom > formData.dateTo) {
      setError('Start date must be before end date');
      return;
    }

    setExporting(true);

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(API_ENDPOINTS.ADMIN.DATA_EXPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          dataType: formData.dataType,
          format: formData.format,
          filters: {
            dateFrom: formData.dateFrom,
            dateTo: formData.dateTo,
            fields: formData.fields
          },
          requestedBy: user.userId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create export');
      }

      const result = await response.json();

      if (result.isAsync) {
        // Large export - async processing
        setActiveJob(result.job);
        setSuccess('Export job created. Processing large dataset...');
      } else {
        // Small export - immediate download
        if (result.downloadUrl) {
          window.open(result.downloadUrl, '_blank');
          setSuccess('Export completed! Download started.');
        } else {
          // Direct data download
          const blob = new Blob([result.data], { 
            type: formData.format === 'csv' ? 'text/csv' : 'application/json' 
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `export-${formData.dataType}-${Date.now()}.${formData.format}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          setSuccess('Export completed! Download started.');
        }
      }

      refetch();
    } catch (err) {
      setError(err.message || 'Failed to create export');
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = (job) => {
    if (job.downloadUrl) {
      window.open(job.downloadUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="data-export">
        <div className="data-export__header">
          <h1 className="data-export__title">Data Export</h1>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height="80px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="data-export">
        <div className="data-export__header">
          <h1 className="data-export__title">Data Export</h1>
        </div>

        {error && (
          <div className="data-export__alert data-export__alert--error">
            {error}
          </div>
        )}

        {success && (
          <div className="data-export__alert data-export__alert--success">
            {success}
          </div>
        )}

        {fetchError && (
          <div className="data-export__alert data-export__alert--error">
            Failed to load export jobs: {fetchError}
          </div>
        )}

        {activeJob && (
          <div className="data-export__active-job">
            <h3>Processing Export Job</h3>
            <p>Job ID: {activeJob.jobId}</p>
            <p>Status: {activeJob.status}</p>
            <div className="data-export__progress">
              <div className="data-export__progress-bar"></div>
            </div>
            <p className="data-export__progress-text">
              Processing {activeJob.recordCount || 'unknown'} records...
            </p>
          </div>
        )}

        <div className="data-export__content">
          <div className="data-export__form-section">
            <h2 className="data-export__section-title">Create New Export</h2>
            
            <form onSubmit={handleSubmit} className="data-export__form">
              <div className="data-export__form-row">
                <div className="data-export__form-group">
                  <label htmlFor="dataType" className="data-export__label">
                    Data Type *
                  </label>
                  <select
                    id="dataType"
                    name="dataType"
                    value={formData.dataType}
                    onChange={handleDataTypeChange}
                    className="data-export__select"
                    required
                  >
                    <option value="users">Users</option>
                    <option value="analytics">Analytics</option>
                    <option value="transactions">Transactions</option>
                    <option value="audit_logs">Audit Logs</option>
                  </select>
                </div>

                <div className="data-export__form-group">
                  <label htmlFor="format" className="data-export__label">
                    Format *
                  </label>
                  <select
                    id="format"
                    name="format"
                    value={formData.format}
                    onChange={handleInputChange}
                    className="data-export__select"
                    required
                  >
                    <option value="csv">CSV</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
              </div>

              <div className="data-export__form-row">
                <div className="data-export__form-group">
                  <label htmlFor="dateFrom" className="data-export__label">
                    Date From
                  </label>
                  <input
                    type="date"
                    id="dateFrom"
                    name="dateFrom"
                    value={formData.dateFrom}
                    onChange={handleInputChange}
                    className="data-export__input"
                  />
                </div>

                <div className="data-export__form-group">
                  <label htmlFor="dateTo" className="data-export__label">
                    Date To
                  </label>
                  <input
                    type="date"
                    id="dateTo"
                    name="dateTo"
                    value={formData.dateTo}
                    onChange={handleInputChange}
                    className="data-export__input"
                  />
                </div>
              </div>

              <div className="data-export__form-group">
                <div className="data-export__fields-header">
                  <label className="data-export__label">
                    Fields to Export *
                  </label>
                  <div className="data-export__fields-actions">
                    <button
                      type="button"
                      onClick={handleSelectAllFields}
                      className="data-export__field-action"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllFields}
                      className="data-export__field-action"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="data-export__fields">
                  {(fieldOptions[formData.dataType] || []).map(field => (
                    <label key={field} className="data-export__field-option">
                      <input
                        type="checkbox"
                        checked={formData.fields.includes(field)}
                        onChange={() => handleFieldToggle(field)}
                        className="data-export__checkbox"
                      />
                      <span>{field}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="data-export__form-actions">
                <button
                  type="submit"
                  className="data-export__btn data-export__btn--primary"
                  disabled={exporting || !!activeJob}
                >
                  {exporting ? 'Creating Export...' : 'Create Export'}
                </button>
              </div>
            </form>
          </div>

          <div className="data-export__history-section">
            <h2 className="data-export__section-title">Export History</h2>
            
            {jobs.length === 0 ? (
              <div className="data-export__empty">
                No exports yet. Create your first export above.
              </div>
            ) : (
              <div className="data-export__jobs">
                {jobs.map((job) => (
                  <div key={job.jobId} className="data-export__job">
                    <div className="data-export__job-header">
                      <div className="data-export__job-info">
                        <h3 className="data-export__job-title">
                          {job.dataType} Export
                        </h3>
                        <span className="data-export__job-date">
                          {new Date(job.requestedAt).toLocaleString()}
                        </span>
                      </div>
                      <StatusBadge status={job.status} />
                    </div>
                    
                    <div className="data-export__job-details">
                      <span className="data-export__job-detail">
                        <strong>Format:</strong> {job.format.toUpperCase()}
                      </span>
                      <span className="data-export__job-detail">
                        <strong>Records:</strong> {job.recordCount || 'N/A'}
                      </span>
                      <span className="data-export__job-detail">
                        <strong>Requested by:</strong> {job.requestedBy}
                      </span>
                    </div>

                    {job.status === 'completed' && job.downloadUrl && (
                      <div className="data-export__job-actions">
                        <button
                          onClick={() => handleDownload(job)}
                          className="data-export__btn data-export__btn--download"
                        >
                          Download
                        </button>
                      </div>
                    )}

                    {job.status === 'failed' && job.errorMessage && (
                      <div className="data-export__job-error">
                        Error: {job.errorMessage}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default DataExport;
